import { queryOptions, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { Account, Category, Period, Statement } from "../types";
import { keys } from "./keys";
import { useMoneyMutation } from "./money";

// ---------------------------------------------------------------- reference data

/** Options for a query a component keeps mounted while hidden (the quick add sheet): with
 * `subscribed: false` it reads the cache but isn't "active", so money changes don't refetch it
 * until it's shown again. */
export interface Passive {
  subscribed?: boolean;
}

// Quick add needs both even when the signal drops later (#42): they're loaded once the app
// shows and kept in memory (no gcTime) instead of being dropped while no screen uses them
const accountsQuery = queryOptions({
  queryKey: keys.accounts,
  queryFn: () => api.get<Account[]>("/accounts"),
  gcTime: Infinity,
});

const categoriesQuery = queryOptions({
  queryKey: keys.categories,
  queryFn: () => api.get<Category[]>("/categories"),
  staleTime: 5 * 60_000,
  gcTime: Infinity,
});

export const useAccounts = ({ subscribed }: Passive = {}) =>
  useQuery({ ...accountsQuery, subscribed });

export const useCategories = () => useQuery(categoriesQuery);

/** What quick add's form needs, loaded ahead so it also opens without signal. */
export function prefetchQuickAdd(qc: QueryClient): void {
  void qc.prefetchQuery(accountsQuery);
  void qc.prefetchQuery(categoriesQuery);
}

export const usePeriods = () =>
  useQuery({
    queryKey: keys.periods,
    queryFn: () => api.get<Period[]>("/periods", { count: 18 }),
    staleTime: 60 * 60_000,
  });

export const useSaveAccount = () =>
  useMoneyMutation((a: Partial<Account> & { id?: number; balance_anchor?: string }) =>
    a.id ? api.patch<Account>(`/accounts/${a.id}`, a) : api.post<Account>("/accounts", a),
  );

export const useSetBalance = () =>
  useMoneyMutation((v: { id: number; balance: string }) =>
    api.put<Account>(`/accounts/${v.id}/balance`, { balance: v.balance }),
  );

export const useStatements = (accountId: number | undefined) =>
  useQuery({
    queryKey: keys.statements(accountId ?? 0),
    queryFn: () => api.get<Statement[]>("/statements", { account_id: accountId }),
    enabled: accountId !== undefined,
  });

export type CardStatementInput = Pick<
  Statement,
  | "cut_off_date"
  | "due_date"
  | "balance"
  | "no_interest_amount"
  | "minimum_payment"
  | "charges"
  | "available_credit"
>;

export const useSetCardStatement = () =>
  useMoneyMutation((v: { id: number } & CardStatementInput) => {
    const { id, ...body } = v;
    return api.put<Statement>(`/accounts/${id}/statement`, body);
  });

/** Manual "Pagado" tag on a statement, keyed by the card's own cut-off date (not the periodo). */
export const useSetStatementPaid = () =>
  useMoneyMutation((v: { id: number; cut_off_date: string; paid: boolean }) => {
    const { id, ...body } = v;
    return api.put<Statement>(`/accounts/${id}/statement/paid`, body);
  });

export const useDeleteAccount = () =>
  useMoneyMutation((id: number) => api.delete(`/accounts/${id}`));

// renaming or moving a category changes labels and per-category totals, so refresh money views too
export function useSaveCategory() {
  const qc = useQueryClient();
  return useMoneyMutation(
    (c: Partial<Category> & { id?: number }) =>
      c.id ? api.patch<Category>(`/categories/${c.id}`, c) : api.post<Category>("/categories", c),
    { onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }) },
  );
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMoneyMutation((id: number) => api.delete(`/categories/${id}`), {
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}
